import { useState } from "react";
import { useDispatch } from "react-redux";
import { capitalize } from "lodash";

import { SetAdvisor } from "data/advisor/actions";
import { SetHubToLoad } from "data/hub/actions";
import { useSync } from "data/hub/sync/useSync";
import { GetAdvisor } from "data/libs/advisor";

import { useAsyncCall, useBooleanState, useMutation } from "hooks";
import { useFeatures } from "components/FeatureFlag";
import { createResolverFactory } from "components/QueryClient/ResolverFactory";

function mutationFactory() {
  const query = `mutation connectCrmMutation($crmType: CrmType!, $token: String!, $shouldUpdateSetupComplete: Boolean) {
                  connectCrm(crmType: $crmType, token: $token, shouldUpdateSetupComplete: $shouldUpdateSetupComplete) {
                    advisor {
                      uuid
                    }
                  }
                }`;

  const resolver = ({ data }) => {
    return {
      advisorId: data.connectCrm.advisor.uuid,
    };
  };

  return { query: query, resolver: createResolverFactory(resolver) };
}

const generateErrorMessage = (err, crmType) => {
  if (Array.isArray(err) && err[0]?.message) return err[0].message;

  return `Failed connect your account to ${capitalize(
    crmType
  )}. Please retry or contact support for assistance.`;
};

export function useIntegrationAction(onDone) {
  const addUserOnboarding = useFeatures("add-user-during-onboarding");

  const [call, connecting] = useAsyncCall();
  const [error, setError] = useState();
  const { runFullSync } = useSync();
  const dispatch = useDispatch();
  const connectCrm = useMutation(mutationFactory);
  const [isLoadingAdvisor, setLoadingAdvisor, stopLoadingAdvisor] =
    useBooleanState(false);

  return {
    connecting: connecting || isLoadingAdvisor,
    error: error,
    connect: (crmType, token) => {
      return call(async () => {
        try {
          const shouldUpdateSetupComplete = !addUserOnboarding?.enable;
          const { advisorId } = await connectCrm({
            crmType,
            token,
            shouldUpdateSetupComplete,
          });

          setLoadingAdvisor();
          const advisor = await GetAdvisor(advisorId);

          dispatch(SetAdvisor(advisor));
          dispatch(SetHubToLoad(advisor.hubs[0]));
          runFullSync();
          onDone();
        } catch (err) {
          setError(generateErrorMessage(err, crmType));
        } finally {
          stopLoadingAdvisor();
        }
      });
    },
  };
}

export default useIntegrationAction;
